"use client"; // optional – add if you need client-side interactivity

import clsx from "clsx";
import React from "react";
import Button from "./Button";
import CommonCard from "./CommonCard";
import SectionTitle from "./SectionTitle";

type CardData = {
  number?: string | number;
  title?: string;
  description?: string;
  highlight?: string;
  iconSrc?: string;
  iconAlt?: string;
  icon?: React.ComponentType<{ className?: string }>;
  contentAlign?: "left" | "center" | "right";
};

type CardSectionProps = {
  sectionTitle?: string;
  sectionSubtitle?: string;
  sectionDescription?: string;
  cards?: CardData[];
  buttonText?: string;
  buttonOnClick?: () => void;
  showFrameworksTitle?: boolean;
};

const CardSection: React.FC<CardSectionProps> = ({
  sectionTitle,
  sectionSubtitle,
  sectionDescription,
  cards = [],
  buttonText,
  buttonOnClick,
  showFrameworksTitle = true,
}) => {
  const displayCards = cards.slice(0, 8);
  const cardCount = Math.max(3, Math.min(displayCards.length, 8));

  const getZigzagTransform = (index: number) => {
    return index % 2 === 0 ? "-translate-y-8" : "translate-y-8";
  };

  const getGridCols = (count: number) => {
    return clsx(
      "grid gap-8 items-stretch",
      count === 3 && "grid-cols-3",
      count === 4 && "grid-cols-4",
      count === 5 && "grid-cols-5",
      count === 6 && "grid-cols-6"
    );
  };

  return (
    <section className="custom-container">
      {(sectionTitle || sectionSubtitle || sectionDescription) && (
        <SectionTitle
          title={sectionTitle || ""}
          subtitle={sectionSubtitle || ""}
          description={sectionDescription || ""}
          align="center"
        />
      )}

      {showFrameworksTitle && (
        <div className="uppercase text-xl 2xl:text-2xl text-center xl:my-12 my-6 font-semibold text-[#272d57]">
          Frameworks Used
        </div>
      )}

      {/* Desktop Layout */}
      <div className="hidden xl:block my-24">
        {cardCount <= 6 ? (
          <div className={getGridCols(cardCount)}>
            {displayCards.map((card, idx) => (
              <div
                key={idx}
                className={clsx(
                  "relative transition-transform duration-500 ease-in-out",
                  getZigzagTransform(idx)
                )}
              >
                <CommonCard
                  {...(card.number && { number: card.number })}
                  title={card.title || ""}
                  {...(card.description && { description: card.description })}
                  {...(card.highlight && { highlight: card.highlight })}
                  {...(card.iconSrc && { iconSrc: card.iconSrc })}
                  {...(card.iconAlt && { iconAlt: card.iconAlt })}
                  {...(card.icon && { icon: card.icon })}
                  contentAlign="center"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            <div className="grid grid-cols-4 gap-8">
              {displayCards.slice(0, 4).map((card, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    "relative transition-transform duration-500 ease-in-out",
                    getZigzagTransform(idx)
                  )}
                >
                  <CommonCard
                    {...(card.number && { number: card.number })}
                    title={card.title || ""}
                    {...(card.description && { description: card.description })}
                    {...(card.highlight && { highlight: card.highlight })}
                    {...(card.iconSrc && { iconSrc: card.iconSrc })}
                    {...(card.iconAlt && { iconAlt: card.iconAlt })}
                    contentAlign="center"
                  />
                </div>
              ))}
            </div>
            <div
              className={clsx(
                "grid gap-8",
                cardCount === 7 ? "grid-cols-3" : "grid-cols-4"
              )}
            >
              {displayCards.slice(4, cardCount).map((card, idx) => (
                <div
                  key={idx + 4}
                  className={clsx(
                    "relative transition-transform duration-500 ease-in-out",
                    getZigzagTransform(idx + 4)
                  )}
                >
                  <CommonCard
                    {...(card.number && { number: card.number })}
                    title={card.title || ""}
                    {...(card.description && { description: card.description })}
                    {...(card.highlight && { highlight: card.highlight })}
                    {...(card.iconSrc && { iconSrc: card.iconSrc })}
                    {...(card.iconAlt && { iconAlt: card.iconAlt })}
                    contentAlign="center"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tablet Layout */}
      <div className="hidden md:block xl:hidden xl:my-12">
        <div
          className={clsx(
            "grid gap-6 items-stretch",
            cardCount <= 4 ? "grid-cols-2" : "grid-cols-3"
          )}
        >
          {displayCards.map((card, idx) => (
            <div key={idx} className="relative">
              <CommonCard
                {...(card.number && { number: card.number })}
                title={card.title || ""}
                {...(card.description && { description: card.description })}
                {...(card.highlight && { highlight: card.highlight })}
                {...(card.iconSrc && { iconSrc: card.iconSrc })}
                {...(card.iconAlt && { iconAlt: card.iconAlt })}
                {...(card.contentAlign && { contentAlign: card.contentAlign })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Layout – NOW WITH FORCED CENTER ALIGNMENT */}
      <div className="block md:hidden my-12">
        <div className="flex flex-col gap-6">
          {displayCards.map((card, idx) => (
            <div key={idx} className="relative">
              <CommonCard
                {...(card.number && { number: card.number })}
                title={card.title || ""}
                {...(card.description && { description: card.description })}
                {...(card.highlight && { highlight: card.highlight })}
                {...(card.iconSrc && { iconSrc: card.iconSrc })}
                {...(card.iconAlt && { iconAlt: card.iconAlt })}
                contentAlign="center"  // ✅ forces center alignment on mobile
              />
            </div>
          ))}
        </div>
      </div>

      {buttonText && (
        <div className="mx-auto text-center my-12">
          <Button
            responsiveSize={{ mobile: "md", tablet: "md", desktop: "lg" }}
            variant="primary"
            {...(buttonOnClick && { onClick: buttonOnClick })}
            className="lg:text-lg"
          >
            {buttonText}
          </Button>
        </div>
      )}
    </section>
  );
};

export default CardSection;